import {
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    Folder as FolderIcon,
    Group as GroupIcon,
    Lock as LockIcon,
    NavigateNext as NavigateNextIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    PersonOutline as UserIcon,
} from '@mui/icons-material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import fileSystemAccessServiceInstance from '../../services/FileSystemAccessService';
import folderPermissionService from '../../services/FolderPermissionService';
import FolderPermissionsDialog from '../dialogs/FolderPermissionsDialog';
import AdminPageWrapper from './AdminPageWrapper';
import UserAccessExplanation from './UserAccessExplanation';

const FolderPermissionManagement = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const [loading, setLoading] = useState(true);
    const [folders, setFolders] = useState([]);
    const [folderPermissions, setFolderPermissions] = useState({});
    const [currentPath, setCurrentPath] = useState('/');
    const [breadcrumbs, setBreadcrumbs] = useState([{ name: 'Root', path: '/' }]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [expandedFolders, setExpandedFolders] = useState({});
    const [loadingPermissions, setLoadingPermissions] = useState({});
    const [rootFolderPermissions, setRootFolderPermissions] = useState(null);
    const [loadingRootPermissions, setLoadingRootPermissions] = useState(false);
    const [accessExplanationOpen, setAccessExplanationOpen] = useState(false);
    const [selectedFolderForAccess, setSelectedFolderForAccess] = useState(null);
    const [usernameForAccess, setUsernameForAccess] = useState('');

    // Add a new state for the username input dialog
    const [usernameInputDialogOpen, setUsernameInputDialogOpen] = useState(false);
    const [tempFolder, setTempFolder] = useState(null);
    const [usernameInput, setUsernameInput] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        loadFolders(currentPath);

        // Load root folder permissions on component mount
        if (currentPath === '/') {
            loadRootFolderPermissions();
        }
    }, [currentPath]);

    const loadFolders = async (path) => {
        setLoading(true);
        setError(null);

        try {
            const userData = JSON.parse(localStorage.getItem('authUser'));
            if (!userData) {
                setError('User data not found');
                setLoading(false);
                return;
            }

            const response = await fileSystemAccessServiceInstance.getFolderContents(
                path,
                userData.email,
                userData.access,
            );

            // Filter to only show folders
            const folderItems = (response.data || [])
                .filter((item) => item && item.isFolder === true)
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

            setFolders(folderItems);
            updateBreadcrumbs(path);

            // Load permissions for all folders
            await loadAllFolderPermissions(folderItems);
        } catch (err) {
            console.error('Error loading folders:', err);
            setError('Failed to load folders. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const loadAllFolderPermissions = async (folderItems) => {
        try {
            // Create a temporary object to track loading state for each folder
            const tempLoadingState = {};
            folderItems.forEach((folder) => {
                const folderId = folder.id || folder._id;
                tempLoadingState[folderId] = true;
            });
            setLoadingPermissions(tempLoadingState);

            // Load permissions for each folder in parallel
            const permissionsPromises = folderItems.map((folder) => loadFolderPermission(folder));

            await Promise.all(permissionsPromises);
        } catch (error) {
            console.error('Error loading all folder permissions:', error);
        }
    };

    const loadFolderPermission = async (folder) => {
        const folderId = folder.id || folder._id;
        try {
            const response = await folderPermissionService.getFolderPermissions(folder.path);

            if (response) {
                setFolderPermissions((prev) => ({
                    ...prev,
                    [folderId]: response,
                }));
            } else {
                console.error('Error loading permissions for folder:', folder.path);
            }
        } catch (error) {
            console.error('Error loading folder permissions:', error);
        } finally {
            setLoadingPermissions((prev) => ({
                ...prev,
                [folderId]: false,
            }));
        }
    };

    const loadRootFolderPermissions = async () => {
        setLoadingRootPermissions(true);
        try {
            const response = await folderPermissionService.getFolderPermissions('/');
            setRootFolderPermissions(response);
        } catch (error) {
            console.error('Error loading root folder permissions:', error);
        } finally {
            setLoadingRootPermissions(false);
        }
    };

    const updateBreadcrumbs = (path) => {
        const parts = path.split('/').filter(Boolean);
        let currentPath = '/';

        const crumbs = [{ name: 'Root', path: '/' }];

        for (const part of parts) {
            currentPath = `${currentPath}${part}/`;
            crumbs.push({ name: part, path: currentPath });
        }

        setBreadcrumbs(crumbs);
    };

    const handleFolderClick = (folder) => {
        const newPath = folder.path.endsWith('/') ? folder.path : `${folder.path}/`;
        setCurrentPath(newPath);
    };

    const handleBreadcrumbClick = (path) => {
        setCurrentPath(path);
    };

    const handleRefresh = () => {
        loadFolders(currentPath);
        if (currentPath === '/') {
            loadRootFolderPermissions();
        }
    };

    const handleBackClick = () => {
        if (currentPath === '/') return;

        // Get parent path
        const parts = currentPath.split('/').filter(Boolean);
        parts.pop(); // Remove last part
        const parentPath = parts.length === 0 ? '/' : `/${parts.join('/')}/`;

        setCurrentPath(parentPath);
    };

    const handleManagePermissions = (folder) => {
        setSelectedFolder(folder);
        setPermissionsDialogOpen(true);
    };

    const handleManageRootPermissions = () => {
        setSelectedFolder({ name: 'Root', path: '/' });
        setPermissionsDialogOpen(true);
    };

    const toggleFolderExpand = (folderId) => {
        setExpandedFolders((prev) => ({
            ...prev,
            [folderId]: !prev[folderId],
        }));
    };

    // Replace the handleCheckUserAccess function
    const handleCheckUserAccess = (folder) => {
        setTempFolder(folder);
        setUsernameInput('');
        setUsernameInputDialogOpen(true);
    };

    // Add a function to handle the dialog submission
    const handleUsernameInputSubmit = () => {
        if (usernameInput.trim()) {
            setSelectedFolderForAccess(tempFolder);
            setUsernameForAccess(usernameInput.trim());
            setAccessExplanationOpen(true);
        }
        setUsernameInputDialogOpen(false);
    };

    const filteredFolders = folders.filter((folder) => folder.name.toLowerCase().includes(searchTerm.toLowerCase()));

    // Helper function to render inheritance status
    const renderInheritanceStatus = (folder) => {
        const folderId = folder.id || folder._id;
        const permissions = folderPermissions[folderId];

        if (loadingPermissions[folderId]) {
            return <Chip label="Loading..." size="small" color="default" variant="outlined" />;
        }

        if (!permissions) {
            return <Chip label="Unknown" size="small" color="default" variant="outlined" />;
        }

        return (
            <Chip
                label={permissions.inherit ? 'Yes' : 'No'}
                size="small"
                color={permissions.inherit ? 'success' : 'warning'}
                variant="outlined"
            />
        );
    };

    // Helper function to render permission counts
    const renderPermissionCounts = (folder, type) => {
        const folderId = folder.id || folder._id;
        const permissions = folderPermissions[folderId];

        if (loadingPermissions[folderId]) {
            return <Chip label="Loading..." size="small" color="default" variant="outlined" />;
        }

        if (!permissions) {
            return (
                <Chip
                    label="View"
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={() => handleManagePermissions(folder)}
                />
            );
        }

        const permArray = type === 'user' ? permissions.userPermissions || [] : permissions.groupPermissions || [];

        const permCount = permArray.length;

        return (
            <Chip
                icon={type === 'user' ? <UserIcon /> : <GroupIcon />}
                label={`${permCount} ${type === 'user' ? 'Users' : 'Groups'}`}
                size="small"
                color={permCount > 0 ? 'primary' : 'default'}
                variant="outlined"
                onClick={() => handleManagePermissions(folder)}
            />
        );
    };

    // Helper function to render detailed permission info in expanded view
    const renderDetailedPermissions = (folder) => {
        const folderId = folder.id || folder._id;
        const permissions = folderPermissions[folderId];

        if (loadingPermissions[folderId]) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            );
        }

        if (!permissions) {
            return (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No permission data available. Click "Manage Permissions" to configure.
                </Alert>
            );
        }

        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Inheritance:{' '}
                    {permissions.inherit ? 'Inheriting permissions from parent' : 'Using custom permissions'}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Typography variant="subtitle2" gutterBottom>
                    User Permissions:
                </Typography>
                {permissions.userPermissions && Object.keys(permissions.userPermissions).length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {Object.entries(permissions.userPermissions).map(([email, access], idx) => (
                            <Chip key={idx} label={`${email}: ${access}`} size="small" color="primary" />
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No user permissions configured
                    </Typography>
                )}

                <Typography variant="subtitle2" gutterBottom>
                    Group Permissions:
                </Typography>
                {permissions.groupPermissions && Object.keys(permissions.groupPermissions).length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Object.entries(permissions.groupPermissions).map(([groupName, access], idx) => (
                            <Chip key={idx} label={`${groupName}: ${access}`} size="small" color="secondary" />
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No group permissions configured
                    </Typography>
                )}
            </Box>
        );
    };

    // Helper function to render user permissions in the table
    const renderUserPermissionsInline = (folder) => {
        const folderId = folder.id || folder._id;
        const permissions = folderPermissions[folderId];

        if (loadingPermissions[folderId]) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        Loading...
                    </Typography>
                </Box>
            );
        }

        // Ensure userPermissions is a valid array
        const userPermissions = permissions.userPermissions;
        const permissionsCount = Object.keys(userPermissions).length;

        if (permissionsCount === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No users
                </Typography>
            );
        }

        // Limit display to first 3 users with a "+X more" indicator
        const permissionsEntries = Object.entries(userPermissions);
        console.log('userPermissions', permissions.userPermissions);
        const displayCount = 2;
        const displayUsers = permissionsEntries.slice(0, displayCount);
        const remainingCount = permissionsEntries.length - displayCount;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {displayUsers.map((perm, idx) => (
                    <Chip
                        key={idx}
                        size="small"
                        label={
                            <Box
                                component="span"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mr: 1,
                                        maxWidth: '120px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {perm.email}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 'bold',
                                        color:
                                            perm.access === 'readonly'
                                                ? 'info.main'
                                                : perm.access === 'readwrite'
                                                ? 'success.main'
                                                : 'warning.main',
                                    }}
                                >
                                    {perm.access === 'readonly'
                                        ? 'Read'
                                        : perm.access === 'readwrite'
                                        ? 'Write'
                                        : 'Admin'}
                                </Typography>
                            </Box>
                        }
                        variant="outlined"
                        color="primary"
                        sx={{
                            height: '24px',
                            '& .MuiChip-label': {
                                px: 1,
                                width: '100%',
                            },
                        }}
                    />
                ))}
                {remainingCount > 0 && (
                    <Chip
                        size="small"
                        label={`+${remainingCount} more`}
                        variant="outlined"
                        color="default"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleFolderExpand(folderId);
                        }}
                        sx={{ height: '24px' }}
                    />
                )}
            </Box>
        );
    };

    // Helper function to render group permissions in the table
    const renderGroupPermissionsInline = (folder) => {
        const folderId = folder.id || folder._id;
        const permissions = folderPermissions[folderId];

        if (loadingPermissions[folderId]) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        Loading...
                    </Typography>
                </Box>
            );
        }

        // Ensure userPermissions is a valid array
        const groupPermissions = permissions.groupPermissions;
        const permissionsCount = Object.keys(groupPermissions).length;

        if (permissionsCount === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No groups
                </Typography>
            );
        }

        // Get entries as array of [key, value] pairs
        const permissionsEntries = Object.entries(groupPermissions);

        // Limit display to first 2 groups with a "+X more" indicator
        const displayCount = 2;
        const displayGroups = permissionsEntries.slice(0, displayCount);
        const remainingCount = permissionsEntries.length - displayCount;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {displayGroups.map((perm, idx) => (
                    <Chip
                        key={idx}
                        size="small"
                        label={
                            <Box
                                component="span"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mr: 1,
                                        maxWidth: '120px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {perm.groupName}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 'bold',
                                        color:
                                            perm.access === 'readonly'
                                                ? 'info.main'
                                                : perm.access === 'readwrite'
                                                ? 'success.main'
                                                : 'warning.main',
                                    }}
                                >
                                    {perm.access === 'readonly'
                                        ? 'Read'
                                        : perm.access === 'readwrite'
                                        ? 'Write'
                                        : 'Admin'}
                                </Typography>
                            </Box>
                        }
                        variant="outlined"
                        color="secondary"
                        sx={{
                            height: '24px',
                            '& .MuiChip-label': {
                                px: 1,
                                width: '100%',
                            },
                        }}
                    />
                ))}
                {remainingCount > 0 && (
                    <Chip
                        size="small"
                        label={`+${remainingCount} more`}
                        variant="outlined"
                        color="default"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleFolderExpand(folderId);
                        }}
                        sx={{ height: '24px' }}
                    />
                )}
            </Box>
        );
    };

    // Component to display root folder permissions
    const RootFolderPermissionsCard = () => {
        if (currentPath !== '/') return null;

        return (
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">Root Folder Permissions</Typography>
                    </Box>
                    <Button
                        startIcon={<LockIcon />}
                        variant="outlined"
                        onClick={handleManageRootPermissions}
                        disabled={loadingRootPermissions}
                    >
                        Manage Root Permissions
                    </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                {loadingRootPermissions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : !rootFolderPermissions ? (
                    <Alert severity="info">
                        No permission data available for Root folder. Click "Manage Root Permissions" to configure.
                    </Alert>
                ) : (
                    <Box>
                        <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Inheritance:
                                </Typography>
                                <Chip
                                    label={rootFolderPermissions.inherit ? 'Yes' : 'No'}
                                    size="small"
                                    color={rootFolderPermissions.inherit ? 'success' : 'warning'}
                                    variant="outlined"
                                />
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    User Permissions:
                                </Typography>
                                <Chip
                                    label={`${Object.keys(rootFolderPermissions.userPermissions || {}).length} Users`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    icon={<UserIcon />}
                                />
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Group Permissions:
                                </Typography>
                                <Chip
                                    label={`${Object.keys(rootFolderPermissions.groupPermissions || {}).length} Groups`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    icon={<GroupIcon />}
                                />
                            </Box>
                        </Box>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>View Detailed Permissions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="subtitle2" gutterBottom>
                                    User Permissions:
                                </Typography>
                                {rootFolderPermissions.userPermissions &&
                                Object.keys(rootFolderPermissions.userPermissions).length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {Object.entries(rootFolderPermissions.userPermissions).map(
                                            ([email, access], idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={`${email}: ${access}`}
                                                    size="small"
                                                    color="primary"
                                                />
                                            ),
                                        )}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        No user permissions configured
                                    </Typography>
                                )}

                                <Typography variant="subtitle2" gutterBottom>
                                    Group Permissions:
                                </Typography>
                                {rootFolderPermissions.groupPermissions &&
                                Object.keys(rootFolderPermissions.groupPermissions).length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {Object.entries(rootFolderPermissions.groupPermissions).map(
                                            ([groupName, access], idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={`${groupName}: ${access}`}
                                                    size="small"
                                                    color="secondary"
                                                />
                                            ),
                                        )}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No group permissions configured
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                )}
            </Paper>
        );
    };

    return (
        <AdminPageWrapper
            title="Folder Permission Management"
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            themeVariant={themeVariant}
        >
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Folder Permission Management
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                    Manage access permissions for all folders in the system
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            <RootFolderPermissionsCard />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={handleBackClick} disabled={currentPath === '/'} sx={{ mr: 1 }}>
                            <NavigateNextIcon sx={{ transform: 'rotate(180deg)' }} />
                        </IconButton>

                        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="folder-navigation">
                            {breadcrumbs.map((crumb) => (
                                <Button
                                    key={crumb.path}
                                    color={crumb.path === currentPath ? 'primary' : 'inherit'}
                                    onClick={() => handleBreadcrumbClick(crumb.path)}
                                    startIcon={crumb.path === '/' ? <FolderIcon /> : null}
                                >
                                    {crumb.name}
                                </Button>
                            ))}
                        </Breadcrumbs>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            placeholder="Search folders"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                        />

                        <Button
                            startIcon={<RefreshIcon />}
                            variant="outlined"
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : filteredFolders.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            No folders found in this location
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table sx={{ minWidth: 650 }} size="medium">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Folder Name</TableCell>
                                    <TableCell>Path</TableCell>
                                    <TableCell>Inherits Permissions</TableCell>
                                    <TableCell>User Permissions</TableCell>
                                    <TableCell>Group Permissions</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredFolders.map((folder) => {
                                    const folderId = folder.id || folder._id;
                                    return (
                                        <React.Fragment key={folderId}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover',
                                                        cursor: 'pointer',
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleFolderExpand(folderId);
                                                            }}
                                                            sx={{ mr: 1 }}
                                                        >
                                                            {expandedFolders[folderId] ? (
                                                                <ExpandLessIcon fontSize="small" />
                                                            ) : (
                                                                <ExpandMoreIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                        <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 'medium', cursor: 'pointer' }}
                                                            onClick={() => handleFolderClick(folder)}
                                                        >
                                                            {folder.name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            maxWidth: '150px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {folder.path}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{renderInheritanceStatus(folder)}</TableCell>
                                                <TableCell>{renderUserPermissionsInline(folder)}</TableCell>
                                                <TableCell>{renderGroupPermissionsInline(folder)}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Tooltip title="Navigate to folder">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleFolderClick(folder)}
                                                                color="primary"
                                                            >
                                                                <NavigateNextIcon />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Manage permissions">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleManagePermissions(folder);
                                                                }}
                                                                color="secondary"
                                                            >
                                                                <LockIcon />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="Check user access">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCheckUserAccess(folder);
                                                                }}
                                                                color="info"
                                                            >
                                                                <PersonSearchIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    colSpan={6}
                                                    sx={{
                                                        py: 0,
                                                        bgcolor: 'action.hover',
                                                    }}
                                                >
                                                    <Collapse
                                                        in={expandedFolders[folderId]}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <Box sx={{ p: 2 }}>
                                                            <Typography
                                                                variant="subtitle2"
                                                                gutterBottom
                                                                component="div"
                                                            >
                                                                Permissions Summary
                                                            </Typography>
                                                            {renderDetailedPermissions(folder)}
                                                            <Box
                                                                sx={{
                                                                    mt: 2,
                                                                    display: 'flex',
                                                                    justifyContent: 'flex-end',
                                                                }}
                                                            >
                                                                <Button
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleManagePermissions(folder);
                                                                    }}
                                                                >
                                                                    Manage Permissions
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {selectedFolder && (
                <FolderPermissionsDialog
                    open={permissionsDialogOpen}
                    onClose={() => {
                        setPermissionsDialogOpen(false);
                        // Refresh the data after dialog closes
                        loadFolders(currentPath);
                        // Refresh root permissions if needed
                        if (selectedFolder.path === '/' || currentPath === '/') {
                            loadRootFolderPermissions();
                        }
                    }}
                    folderPath={selectedFolder.path}
                    folderName={selectedFolder.name}
                />
            )}

            {selectedFolderForAccess && (
                <UserAccessExplanation
                    open={accessExplanationOpen}
                    onClose={() => setAccessExplanationOpen(false)}
                    folderPath={selectedFolderForAccess.path}
                    username={usernameForAccess}
                />
            )}

            {/* Add this dialog component at the end of your return statement */}
            <Dialog
                open={usernameInputDialogOpen}
                onClose={() => setUsernameInputDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Check User Access</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                        Enter a username or email to check their access to{' '}
                        {tempFolder ? <strong>{tempFolder.name}</strong> : 'this folder'}.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Username or Email"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={usernameInput}
                        onChange={(e) => setUsernameInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleUsernameInputSubmit();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUsernameInputDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleUsernameInputSubmit} color="primary" disabled={!usernameInput.trim()}>
                        Check Access
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminPageWrapper>
    );
};

export default FolderPermissionManagement;
